/**
 * Recurring documents — the web-side schedule vocabulary.
 *
 * The DB owns the rule (`recurring_next_occurrence_date`) and the TS twin in
 * `@releve/shared/recurring/schedule` mirrors it for the preview; this module
 * is everything the FORMS and the GRID need around that twin:
 *
 *  - `recurrenceFormSchema` / `recurrenceFormSchemaFor` — the zod shape of the
 *    "Make this recurring" block (`recurrence` sub-object in each create form).
 *    Error messages are i18n KEYS (`recurring.form.errors.*`), rendered inline
 *    beside the label with `t(fieldState.error.message)`.
 *  - `toTemplateRule` / `toCreateTemplateInput` / `fromTemplate` — form values
 *    ⇄ the wire body `POST/PATCH /api/v1/recurring/orgs/:orgId/templates`.
 *  - `describeRecurrence` — the words the section, the grid and the panel all
 *    print for one schedule ("Every 2 months on the 31st (or last day) · due
 *    30 days after"), composed from the `recurring.describe.*` +
 *    `recurring.ordinal.*` key contract in `locales/en/common.json`.
 *  - `todayInOrgZone` — the ONE "today" the form uses. It is day-math over an
 *    instant (docs/DATES.md), deliberately mirroring the DB's
 *    `(now() AT TIME ZONE COALESCE(o.timezone,'UTC'))::date`, so the browser
 *    and `create_recurring_document_template`'s `start >= org-today` guard
 *    agree even when the user's laptop is in another zone. The occurrence
 *    date itself stays a plain calendar day.
 *
 * No i18n lives in `@releve/shared` (nothing there takes a `t`) — this is the
 * one place a schedule is turned into words.
 */
import { z } from 'zod';
import { addDays, addMonths, differenceInCalendarDays, getDaysInMonth, setDate } from 'date-fns';
import {
  RECURRING_FREQUENCIES,
  anchorDayOf,
  calendarDateSchema,
  maxIntervalCountFor,
  type CreateRecurringTemplateInput,
  type RecurringDocumentType,
  type RecurringFrequency,
  type RecurringTemplateBaseWire,
  type RecurringTemplatePayloadByType,
} from '@releve/shared';
import { formatLocalISODate, formatOrgDay, parseLocalISODate } from '@/lib/dateUtils';
import { PAYMENT_TERMS_OPTIONS } from '@/lib/paymentTerms';

export { RECURRING_FREQUENCIES, maxIntervalCountFor };
export type { RecurringFrequency };

/** `(key, vars) => string` — `useTranslation().t` or a mapper's `TranslateFn`. */
export type RecurrenceTranslateFn = (key: string, vars?: Record<string, unknown>) => string;

// ---------------------------------------------------------------------------
// Text tokens + defaults the forms surface
// ---------------------------------------------------------------------------

/**
 * The tokens `recurring_render_text` substitutes at generation, in
 * description / reference / line descriptions / the AP `invoice_number`.
 * They are `{{…}}`, which is ALSO i18next's interpolation syntax — so a
 * translation string never spells them out; it takes `{{tokens}}` /
 * `{{example}}` and the caller passes these constants as the values
 * (an interpolated VALUE is not re-interpolated).
 */
export const RECURRING_TEXT_TOKENS = ['{{YYYY}}', '{{MM}}', '{{DD}}'] as const;
export const RECURRING_TEXT_TOKENS_HINT_VALUE = RECURRING_TEXT_TOKENS.join(' ');
/** The bill number the AP form suggests when the recurring box is ticked. */
export const AP_BILL_NUMBER_TOKEN_DEFAULT = 'BILL-{{YYYY}}{{MM}}';

/** Document types whose generated document has a due date (`due_days` NOT NULL). */
export const RECURRING_TYPES_WITH_DUE_DAYS: readonly RecurringDocumentType[] = ['ap_bill', 'ar_invoice'];

export function requiresDueDays(documentType: RecurringDocumentType): boolean {
  return RECURRING_TYPES_WITH_DUE_DAYS.includes(documentType);
}

// ---------------------------------------------------------------------------
// Org-today
// ---------------------------------------------------------------------------

/**
 * Today's calendar day in the organization's zone, `YYYY-MM-DD`.
 *
 * `formatOrgDay` takes the `Date` path of `dbCalendarDayInZone`; with a null
 * zone THAT falls back to the browser's day, so the `?? 'UTC'` here mirrors
 * the DB's `COALESCE(o.timezone, 'UTC')` — both sides resolve the same day.
 */
export function todayInOrgZone(timezone: string | null | undefined, now: Date = new Date()): string {
  return formatOrgDay(now, timezone ?? 'UTC', 'yyyy-MM-dd');
}

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

export const RECURRENCE_END_MODES = ['never', 'on_date', 'after_count'] as const;
export type RecurrenceEndMode = (typeof RECURRENCE_END_MODES)[number];


/** A real `YYYY-MM-DD` calendar day — `parseLocalISODate` rolls Feb 30 over, `calendarDateSchema` refuses it. */
const isCalendarDay = (value: string): boolean => calendarDateSchema.safeParse(value).success;

/** A real `YYYY-MM-DD` calendar day (Feb 31 refused), message = i18n key. */
const calendarDayField = z.string().refine(isCalendarDay, 'recurring.form.errors.invalidDate');

/**
 * The `recurrence` sub-object of each create form. Structural rules only —
 * anything that needs runtime context (org-today, the document type) lives
 * in {@link recurrenceFormSchemaFor}. `end_date` / `max_occurrences` /
 * `due_days` are OPTIONAL here so an unticked "Make this recurring" block
 * carries empty controls without tripping validation; the end-mode rules in
 * the superRefine make the one the user picked required.
 *
 * There is deliberately NO `day_of_month` / `weekday` field: the DB derives
 * `anchor_day` from `start_date`, so the form's "On day" / weekday control
 * edits `start_date` through {@link withDayOfMonth} / {@link withWeekday}
 * and reads back through {@link dayOfMonthOf} / {@link weekdayOf}. One
 * field, one truth.
 */
export const recurrenceFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'recurring.form.errors.nameRequired')
      .max(120, 'recurring.form.errors.nameTooLong'),
    frequency: z.enum(RECURRING_FREQUENCIES),
    interval_count: z
      .number({ invalid_type_error: 'recurring.form.errors.intervalMin' })
      .int('recurring.form.errors.intervalMin')
      .min(1, 'recurring.form.errors.intervalMin'),
    start_date: calendarDayField,
    end_mode: z.enum(RECURRENCE_END_MODES),
    /** `''` / undefined = none; validated only under `end_mode = 'on_date'`. */
    end_date: z.string().optional(),
    /** Validated only under `end_mode = 'after_count'`. */
    max_occurrences: z.number().optional(),
    /** AP/AR only — required by {@link recurrenceFormSchemaFor} for those types. */
    due_days: z.number().optional(),
    /**
     * AR only (`recurring_document_templates.auto_issue`, 20261101120000):
     * issue each generated invoice on its occurrence date. Off by default —
     * each occurrence is then a draft to issue by hand.
     */
    auto_issue: z.boolean().optional(),
  })
  .superRefine((values, ctx) => {
    const cap = maxIntervalCountFor(values.frequency);
    if (values.interval_count > cap) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['interval_count'],
        // The key takes `{{max}}` — render with `t(message, { max: maxIntervalCountFor(frequency) })`.
        message: 'recurring.form.errors.intervalMax',
      });
    }
    if (values.end_mode === 'on_date') {
      if (!values.end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['end_date'],
          message: 'recurring.form.errors.endDateRequired',
        });
      } else if (!isCalendarDay(values.end_date)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['end_date'],
          message: 'recurring.form.errors.invalidDate',
        });
      } else if (values.end_date < values.start_date) {
        // Both are YYYY-MM-DD, so string order is date order.
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['end_date'],
          message: 'recurring.form.errors.endBeforeStart',
        });
      }
    }
    if (values.end_mode === 'after_count') {
      const n = values.max_occurrences;
      if (n == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['max_occurrences'],
          message: 'recurring.form.errors.maxOccurrencesRequired',
        });
      } else if (!Number.isInteger(n) || n < 1 || n > 1000) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['max_occurrences'],
          message: 'recurring.form.errors.maxOccurrencesRange',
        });
      }
    }
    if (values.due_days != null && (!Number.isInteger(values.due_days) || values.due_days < 0 || values.due_days > 365)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['due_days'],
        message: 'recurring.form.errors.dueDaysRange',
      });
    }
  });

export type RecurrenceFormValues = z.infer<typeof recurrenceFormSchema>;

export interface RecurrenceFormSchemaContext {
  /** {@link todayInOrgZone} — `start_date` may not be before it. */
  orgToday: string;
  /** AP/AR make `due_days` required. */
  documentType: RecurringDocumentType;
  /**
   * Template-edit mode: the saved `start_date`. An UNCHANGED start is never
   * re-checked against today (a paused, never-run template may legitimately
   * carry a past start), and once `occurrences_generated > 0` the RPC refuses
   * to move it at all — pass `startLocked` so the form says so.
   */
  originalStartDate?: string | null;
  startLocked?: boolean;
}

/**
 * {@link recurrenceFormSchema} plus the context-dependent rules. Memoize per
 * `(orgToday, documentType, originalStartDate, startLocked)` in the form.
 */
export function recurrenceFormSchemaFor(context: RecurrenceFormSchemaContext) {
  const { orgToday, documentType, originalStartDate = null, startLocked = false } = context;
  return recurrenceFormSchema.superRefine((values, ctx) => {
    const startChanged = originalStartDate == null || values.start_date !== originalStartDate;
    if (startChanged && startLocked) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['start_date'],
        message: 'recurring.form.errors.startLocked',
      });
    } else if (startChanged && values.start_date < orgToday) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['start_date'],
        message: 'recurring.form.errors.startBeforeToday',
      });
    }
    if (requiresDueDays(documentType) && values.due_days == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['due_days'],
        message: 'recurring.form.errors.dueDaysRequired',
      });
    }
  });
}

// ---------------------------------------------------------------------------
// Defaults + the day-of-month / weekday control
// ---------------------------------------------------------------------------

export interface DefaultRecurrenceOptions {
  orgToday: string;
  /** The form's document date — the start seeds from it, clamped to org-today. */
  documentDate?: string | null;
  name?: string;
  frequency?: RecurringFrequency;
  /** AP/AR: from {@link dueDaysFromTerms}; ignored for expense/journal. */
  dueDays?: number | null;
  documentType: RecurringDocumentType;
}

/** The values the block shows the moment "Make this recurring" is ticked. */
export function defaultRecurrenceValues(options: DefaultRecurrenceOptions): RecurrenceFormValues {
  const { orgToday, documentDate, name = '', frequency = 'monthly', dueDays, documentType } = options;
  const seed = documentDate && isCalendarDay(documentDate) ? documentDate : orgToday;
  return {
    name,
    frequency,
    interval_count: 1,
    start_date: seed < orgToday ? orgToday : seed,
    end_mode: 'never',
    end_date: undefined,
    max_occurrences: undefined,
    due_days: requiresDueDays(documentType) ? (dueDays ?? 30) : undefined,
    // Off by default (Clay's ruling): an AR occurrence is a draft unless asked.
    auto_issue: documentType === 'ar_invoice' ? false : undefined,
  };
}

/** Day-of-month (1..31) of a `YYYY-MM-DD` start — what the DB stores as `anchor_day`. */
export function dayOfMonthOf(startISO: string): number | null {
  const d = parseLocalISODate(startISO);
  return d ? d.getDate() : null;
}

/** Weekday (0 = Sunday … 6 = Saturday, JS `getDay()`) of a `YYYY-MM-DD` start. */
export function weekdayOf(startISO: string): number | null {
  const d = parseLocalISODate(startISO);
  return d ? d.getDay() : null;
}

/**
 * The "On day" control: move `start_date` to `day` of its month. A day the
 * month does not have (Feb 31) rolls into the NEXT month, which always has it
 * (Mar 31), so the anchor the DB derives is the day the user typed — never a
 * silently clamped 28. With `minISO` (org-today) a result that fell into the
 * past advances a month, so the first occurrence stays a future date.
 */
export function withDayOfMonth(startISO: string, day: number, minISO?: string): string {
  const base = parseLocalISODate(startISO);
  if (!base || !Number.isInteger(day) || day < 1 || day > 31) return startISO;
  let cursor = setDate(base, 1);
  for (let guard = 0; guard < 24; guard += 1) {
    if (day <= getDaysInMonth(cursor)) {
      const candidate = formatLocalISODate(setDate(cursor, day)) ?? startISO;
      if (!minISO || candidate >= minISO) return candidate;
    }
    cursor = addMonths(cursor, 1);
  }
  return startISO;
}

/**
 * The weekly control: the first date on/after `start_date` (or `minISO`, if
 * later) that falls on `weekday` (0 = Sunday … 6). Always moves forward.
 */
export function withWeekday(startISO: string, weekday: number, minISO?: string): string {
  const base = parseLocalISODate(minISO && minISO > startISO ? minISO : startISO);
  if (!base || !Number.isInteger(weekday) || weekday < 0 || weekday > 6) return startISO;
  const delta = (weekday - base.getDay() + 7) % 7;
  return formatLocalISODate(addDays(base, delta)) ?? startISO;
}

/** Sunday-first weekday names in `locale` — index = JS `getDay()`. */
export function weekdayLabels(locale?: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  // 2023-01-01 was a Sunday; local-midnight dates so no zone shift.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2023, 0, 1 + i)));
}

// ---------------------------------------------------------------------------
// Payment terms → due_days
// ---------------------------------------------------------------------------

/**
 * `due_days` for an AP/AR template, seeded from the form's payment terms:
 * a fixed term (`net_30`) is its day count; `custom` (or no term) falls back
 * to `due_date − invoice_date` in calendar days; nothing derivable → null.
 * Always within the column's 0..365.
 */
export function dueDaysFromTerms(
  terms: string | null | undefined,
  invoiceDate: string | null | undefined,
  dueDate: string | null | undefined,
): number | null {
  const term = PAYMENT_TERMS_OPTIONS.find((option) => option.value === terms);
  if (term && term.days !== null) return term.days;
  const from = invoiceDate ? parseLocalISODate(invoiceDate) : null;
  const to = dueDate ? parseLocalISODate(dueDate) : null;
  if (!from || !to) return null;
  const days = differenceInCalendarDays(to, from);
  return Math.min(365, Math.max(0, days));
}

// ---------------------------------------------------------------------------
// Form values ⇄ wire
// ---------------------------------------------------------------------------

/** The schedule half of a template body — everything but `document_type` + `payload`. */
export interface RecurringTemplateScheduleInput {
  name: string;
  frequency: RecurringFrequency;
  interval_count: number;
  start_date: string;
  end_date: string | null;
  max_occurrences: number | null;
  /** Present only for AP/AR (`due_days_by_type`). */
  due_days?: number;
}

/** Form values → the schedule fields of the wire body (end-mode resolved). */
export function toTemplateRule(values: RecurrenceFormValues): RecurringTemplateScheduleInput {
  const rule: RecurringTemplateScheduleInput = {
    name: values.name.trim(),
    frequency: values.frequency,
    interval_count: values.interval_count,
    start_date: values.start_date,
    end_date: values.end_mode === 'on_date' && values.end_date ? values.end_date : null,
    max_occurrences: values.end_mode === 'after_count' && values.max_occurrences != null ? values.max_occurrences : null,
  };
  if (values.due_days != null) rule.due_days = values.due_days;
  return rule;
}

/**
 * The complete `POST /templates` (and `PATCH /templates/:id` — full replace)
 * body for one document type. `payload` comes from `buildRecurringPayload`
 * (`lib/recurringPayload.ts`). `due_days` is REQUIRED for AP/AR — the form
 * schema guarantees it; a missing one here is a programming error, so throw.
 */
export function toCreateTemplateInput<T extends RecurringDocumentType>(
  documentType: T,
  values: RecurrenceFormValues,
  payload: RecurringTemplatePayloadByType[T],
): CreateRecurringTemplateInput {
  const { due_days, ...schedule } = toTemplateRule(values);
  switch (documentType) {
    case 'ap_bill':
    case 'ar_invoice': {
      if (due_days == null) throw new Error(`due_days is required for a recurring ${documentType}`);
      return {
        document_type: documentType,
        ...schedule,
        due_days,
        // AR only — the AP variant's `.strict()` rejects the key.
        ...(documentType === 'ar_invoice' ? { auto_issue: values.auto_issue ?? false } : {}),
        payload: payload as RecurringTemplatePayloadByType['ap_bill' | 'ar_invoice'],
      } as CreateRecurringTemplateInput;
    }
    case 'expense':
      return { document_type: 'expense', ...schedule, payload: payload as RecurringTemplatePayloadByType['expense'] };
    case 'journal':
      return { document_type: 'journal', ...schedule, payload: payload as RecurringTemplatePayloadByType['journal'] };
    default: {
      const never: never = documentType;
      throw new Error(`Unknown recurring document type ${String(never)}`);
    }
  }
}

/**
 * A saved template → form values (template-edit mode). `end_mode` is derived:
 * an `end_date` wins over `max_occurrences` (the form only ever writes one).
 */
export function fromTemplate(
  template: Pick<
    RecurringTemplateBaseWire,
    'name' | 'frequency' | 'interval_count' | 'start_date' | 'end_date' | 'max_occurrences' | 'due_days'
  > &
    Partial<Pick<RecurringTemplateBaseWire, 'auto_issue' | 'document_type'>>,
): RecurrenceFormValues {
  const end_mode: RecurrenceEndMode = template.end_date
    ? 'on_date'
    : template.max_occurrences != null
      ? 'after_count'
      : 'never';
  return {
    name: template.name,
    frequency: template.frequency,
    interval_count: template.interval_count,
    start_date: template.start_date,
    end_mode,
    end_date: template.end_date ?? undefined,
    max_occurrences: template.max_occurrences ?? undefined,
    due_days: template.due_days ?? undefined,
    // AR only; every other type carries `false` on the wire, kept off the form.
    auto_issue: template.document_type === 'ar_invoice' ? (template.auto_issue ?? false) : undefined,
  };
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** What {@link describeRecurrence} needs — a template row or the form's values. */
export interface RecurrenceDescription {
  frequency: RecurringFrequency;
  interval_count: number;
  /** `YYYY-MM-DD` — the weekday (weekly) and the month+day (yearly) come from it. */
  start_date: string;
  /** 1..31; defaults to the day of `start_date` (what the DB derives). */
  anchor_day?: number | null;
  /** AP/AR only — appends "· due N days after". */
  due_days?: number | null;
}

/**
 * "31st" — `Intl.PluralRules(locale, { type: 'ordinal' })` picks the category
 * and `recurring.ordinal.{one,two,few,other}` supplies the suffix, so FR gets
 * "1er / 2e" and NL "1e / 2e" from the same code path.
 */
export function formatOrdinal(day: number, t: RecurrenceTranslateFn, locale?: string): string {
  const category = new Intl.PluralRules(locale, { type: 'ordinal' }).select(day);
  return `${day}${t(`recurring.ordinal.${category}`)}`;
}

/**
 * The schedule in words. Same function behind the section's preview line,
 * the grid's "Repeats" column and the expanded panel's summary, so the three
 * can never disagree. Pieces are joined with spaces and " · " (punctuation,
 * not translated).
 *
 *   monthly ×2, anchor 31, due 30  → "Every 2 months on the 31st (or last day) · due 30 days after"
 *   weekly ×1, start Monday        → "Every week on Monday"
 *   yearly ×1, start 2026-02-28    → "Every year on February 28"
 */
export function describeRecurrence(rule: RecurrenceDescription, t: RecurrenceTranslateFn, locale?: string): string {
  const every = t(`recurring.describe.every.${rule.frequency}`, { count: rule.interval_count });
  const start = parseLocalISODate(rule.start_date);
  const anchor = rule.anchor_day ?? (start ? start.getDate() : null);
  const parts: string[] = [every];

  if (rule.frequency === 'weekly') {
    if (start) {
      const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(start);
      parts.push(t('recurring.describe.onWeekday', { weekday }));
    }
  } else if (rule.frequency === 'yearly') {
    if (start) {
      const date = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric' }).format(start);
      parts.push(t('recurring.describe.onDate', { date }));
    } else if (anchor != null) {
      parts.push(t('recurring.describe.onDay', { ordinal: formatOrdinal(anchor, t, locale) }));
    }
    if (anchor != null && anchor >= 29) parts.push(t('recurring.describe.lastDayNote'));
  } else if (anchor != null) {
    parts.push(t('recurring.describe.onDay', { ordinal: formatOrdinal(anchor, t, locale) }));
    if (anchor >= 29) parts.push(t('recurring.describe.lastDayNote'));
  }

  const schedule = parts.join(' ');
  if (rule.due_days == null) return schedule;
  return `${schedule} · ${t('recurring.describe.dueAfter', { count: rule.due_days })}`;
}

/**
 * The template name the form seeds when the box is ticked:
 * "Acme Hosting (Monthly)" from the payee/customer/description + the
 * frequency word; just the frequency word when there is no base yet.
 */
export function suggestTemplateName(
  base: string | null | undefined,
  frequency: RecurringFrequency,
  t: RecurrenceTranslateFn,
): string {
  const word = t(`recurring.frequency.${frequency}`);
  const trimmed = base?.trim();
  if (!trimmed) return word;
  return t('recurring.form.nameSuggestion', { base: trimmed, frequency: word }).slice(0, 120);
}

/** The DB's `anchor_day` for a start — re-exported so callers need one import. */
export { anchorDayOf };
